import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, AlertDescription, Button, Spinner } from '@/components/ui'
import { useIntegrationsOverview } from '@/hooks/useIntegrationsOverview'
import { StatusChip, HealthChip } from './chips'
import { IntegrationDrawer } from './IntegrationDrawer'

export function IntegrationsHubPage() {
  const overview = useIntegrationsOverview()
  const [selected, setSelected] = useState<{ service: string; source: 'registry' | 'oauth' } | null>(null)

  return (
    <PageFrame width="detail" className="space-y-6">
      <PageHeader
        title="Integrations"
        subtitle="Every service the app can connect to — where it's configured and when it was last verified."
        actions={
          <Link
            to="/admin/credential-sets"
            className="text-sm text-primary hover:underline whitespace-nowrap"
          >
            Advanced: credential sets →
          </Link>
        }
      />

      {overview.isLoading && (
        <div className="flex justify-center py-12">
          <Spinner className="w-8 h-8" />
        </div>
      )}

      {overview.isError && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>
              Couldn't load integrations
              {overview.error instanceof Error ? ` — ${overview.error.message}` : ''}
            </span>
            <Button variant="outline" size="sm" onClick={() => overview.refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {overview.data && (
        <ul className="divide-y divide-border rounded-lg border border-border bg-card">
          {overview.data.map((row) => (
            <li key={`${row.source}:${row.service}`}>
              <button
                type="button"
                onClick={() => setSelected({ service: row.service, source: row.source })}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-accent/50"
              >
                <span className="font-medium text-foreground flex-1 min-w-0 truncate">{row.label}</span>
                <StatusChip row={row} />
                <HealthChip row={row} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {selected && (
        <IntegrationDrawer
          serviceKey={selected.service}
          source={selected.source}
          onClose={() => setSelected(null)}
        />
      )}
    </PageFrame>
  )
}
